import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconButton,
  PrimaryButton,
  DefaultButton,
  TextField,
  Toggle,
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Dialog,
  DialogType,
  DialogFooter,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import { selectEmailTemplatesState } from './emailTemplatesState';
import { RichTextEditor } from '@coherence-design-system/rich-text-editor';
import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
  BulletListButton,
  NumberedListButton,
  CreateLinkButton,
} from '@coherence-design-system/rich-text-editor';
import { CoherenceV9LightTheme } from '@coherence-design-system/styles';
import type { IEditor } from 'roosterjs';
import {
  selectTemplate,
  updateEmailTemplate,
  previewEmailTemplate,
  clearSaveStatus,
  setTemplateField,
} from '../../Redux/EmailTemplates/actions';
import { EmailTemplate, EmailTemplateUpdate } from '../../models/EmailTemplate';
import { VariableInsertPanel } from './VariableInsertPanel';
import { EmailTemplatePreview } from './EmailTemplatePreview';
import { usePermission } from '../../Hooks/useFeature';
import { styles } from './EmailTemplateAdmin.styled';

const ccBccOptions: IDropdownOption[] = [
  { key: 'None', text: 'None' },
  { key: 'Assigned HRC', text: 'Assigned HRC' },
  { key: 'HRC Leader', text: 'HRC Leader' },
  { key: 'PBT Admin Group', text: 'PBT Admin Group' },
];

function getBadgeClass(recipientType: string): string {
  switch (recipientType) {
    case 'Manager':
      return styles.badgeManager;
    case 'Employee':
      return styles.badgeEmployee;
    case 'HRC':
      return styles.badgeHRC;
    default:
      return styles.badgeManager;
  }
}

export const EmailTemplateEditPanel: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedTemplate, saving, saveSuccess, error, correlationId, previewing, previewResult, variables } =
    useSelector(selectEmailTemplatesState);

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const canManageTemplates = usePermission('Email.ManageTemplates');
  const [originalTemplate, setOriginalTemplate] = useState<EmailTemplate | null>(null);
  const roosterEditorRef = useRef<IEditor | null>(null);
  const docHeadRef = useRef<string>('');
  const [editorBody, setEditorBody] = useState<string>('');
  const pendingCloseRef = useRef(false);

  // Reassembles the full HTML email document from the preserved <head> (which carries
  // the template's own <style>/charset) and the <body> content edited in the RTE.
  const buildFullDocument = useCallback((bodyHtml: string): string => {
    const head = docHeadRef.current ? `<head>${docHeadRef.current}</head>` : '';
    return `<!DOCTYPE html><html>${head}<body>${bodyHtml ?? ''}</body></html>`;
  }, []);

  // Close the edit panel automatically once a save initiated from this panel succeeds.
  useEffect(() => {
    if (saveSuccess && pendingCloseRef.current) {
      pendingCloseRef.current = false;
      setOriginalTemplate(null);
      dispatch(clearSaveStatus());
      dispatch(selectTemplate(null));
    }
  }, [saveSuccess, dispatch]);

    useEffect(() => {
        if (!selectedTemplate) {
            setOriginalTemplate(null);
            return;
        }

        // Reset the snapshot when switching templates to keep unsaved-change detection correct.
        setOriginalTemplate({ ...selectedTemplate });
    }, [selectedTemplate?.templateCode]);

    // Sanitize the full email document (preserving <head>/<style> so the template's
    // own CSS applies) and return a complete HTML document string. The body is rendered
    // inside a sandboxed iframe, which isolates the email CSS from the host app and
    // prevents the layout/overlap issues seen when injecting into the app DOM.
    const sanitizeEmailDocumentHtml = useCallback((html: string): string => {
        const doc = new DOMParser().parseFromString(html ?? '', 'text/html');

        // Remove dangerous / external-loading elements. <style> and <meta charset> are kept.
        doc.querySelectorAll('script, iframe, object, embed, link').forEach((el) => el.remove());

        // Remove all <meta> tags except charset metadata (e.g. <meta charset="utf-8">
        // or <meta http-equiv="content-type" content="...; charset=...">). This prevents
        // a non-charset meta (such as http-equiv="refresh") from round-tripping into the
        // saved template HTML.
        doc.querySelectorAll('meta').forEach((meta) => {
            const hasCharsetAttr = meta.hasAttribute('charset');
            const httpEquiv = (meta.getAttribute('http-equiv') || '').toLowerCase();
            const content = (meta.getAttribute('content') || '').toLowerCase();
            const isContentTypeWithCharset = httpEquiv === 'content-type' && content.includes('charset=');

            if (!hasCharsetAttr && !isContentTypeWithCharset) {
                meta.remove();
            }
        });

        // Remove inline event handlers and javascript: URLs
        doc.querySelectorAll('*').forEach((el) => {
            Array.from(el.attributes).forEach((attr) => {
                const name = attr.name.toLowerCase();
                const value = attr.value;

                if (name.startsWith('on')) el.removeAttribute(attr.name);
                if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value)) {
                    el.removeAttribute(attr.name);
                }
            });
        });

        return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
    }, []);

    const readEditorHtml = useCallback((): string | null => {
        return roosterEditorRef.current ? buildFullDocument(roosterEditorRef.current.getContent()) : null;
    }, [buildFullDocument]);

    const templateCode = selectedTemplate?.templateCode;
    useEffect(() => {
        const initialBody = selectedTemplate?.emailBody;
        if (initialBody == null) return;

        // Split the stored full-document template into its <head> (preserved for the
        // email's own <style>/charset) and <body> (edited in the rich text editor).
        const doc = new DOMParser().parseFromString(sanitizeEmailDocumentHtml(initialBody), 'text/html');
        docHeadRef.current = doc.head?.innerHTML ?? '';
        const bodyHtml = doc.body?.innerHTML ?? '';
        setEditorBody(bodyHtml);

        // Canonicalize the stored value + snapshot so unsaved-change detection compares
        // like-for-like and does not report spurious changes on open.
        const canonical = buildFullDocument(bodyHtml);
        dispatch(setTemplateField('emailBody', canonical));
        setOriginalTemplate((prev) => (prev ? { ...prev, emailBody: canonical } : prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateCode, sanitizeEmailDocumentHtml, buildFullDocument]); // Only re-run when template selection changes

    const hasUnsavedChanges = useCallback((): boolean => {
        if (!selectedTemplate || !originalTemplate) return false;
    return (
      selectedTemplate.emailSubject !== originalTemplate.emailSubject ||
      selectedTemplate.emailBody !== originalTemplate.emailBody ||
      selectedTemplate.isActive !== originalTemplate.isActive ||
      JSON.stringify(selectedTemplate.ccRecipients) !== JSON.stringify(originalTemplate.ccRecipients) ||
      JSON.stringify(selectedTemplate.bccRecipients) !== JSON.stringify(originalTemplate.bccRecipients)
    );
  }, [selectedTemplate, originalTemplate]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowDiscardDialog(true);
    } else {
      dispatch(selectTemplate(null));
      setOriginalTemplate(null);
    }
  }, [dispatch, hasUnsavedChanges]);

  const handleDiscard = useCallback(() => {
    setShowDiscardDialog(false);
    dispatch(selectTemplate(null));
    setOriginalTemplate(null);
  }, [dispatch]);

  const handleSave = useCallback(() => {
    if (!selectedTemplate) return;

    const bodyRaw = readEditorHtml() ?? selectedTemplate.emailBody;
    const body = sanitizeEmailDocumentHtml(bodyRaw);

    const update: EmailTemplateUpdate = {
      emailSubject: selectedTemplate.emailSubject,
      emailBody: body,
      ccRecipients: selectedTemplate.ccRecipients,
      bccRecipients: selectedTemplate.bccRecipients,
      isActive: selectedTemplate.isActive,
    };
    dispatch(updateEmailTemplate(selectedTemplate.templateCode, update));
    setOriginalTemplate({ ...selectedTemplate, emailBody: body });
    pendingCloseRef.current = true;
  }, [dispatch, selectedTemplate, sanitizeEmailDocumentHtml, readEditorHtml]);

  const handlePreview = useCallback(() => {
    if (!selectedTemplate) return;
    dispatch(previewEmailTemplate(selectedTemplate.templateCode));
    setShowPreview(true);
  }, [dispatch, selectedTemplate]);

  const handleSubjectChange = useCallback(
    (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
      dispatch(setTemplateField('emailSubject', newValue || ''));
    },
    [dispatch]
  );

  const handleEditorChange = useCallback(
    (newBody: string | undefined) => {
      dispatch(setTemplateField('emailBody', buildFullDocument(newBody ?? '')));
    },
    [dispatch, buildFullDocument]
  );

  const handleCcChange = useCallback(
    (_ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (!option || !selectedTemplate) return;
      const key = option.key as string;
      const current = [...selectedTemplate.ccRecipients];
      if (option.selected) {
        if (!current.includes(key)) current.push(key);
      } else {
        const idx = current.indexOf(key);
        if (idx >= 0) current.splice(idx, 1);
      }
      dispatch(setTemplateField('ccRecipients', current));
    },
    [dispatch, selectedTemplate]
  );

  const handleBccChange = useCallback(
    (_ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (!option || !selectedTemplate) return;
      const key = option.key as string;
      const current = [...selectedTemplate.bccRecipients];
      if (option.selected) {
        if (!current.includes(key)) current.push(key);
      } else {
        const idx = current.indexOf(key);
        if (idx >= 0) current.splice(idx, 1);
      }
      dispatch(setTemplateField('bccRecipients', current));
    },
    [dispatch, selectedTemplate]
  );

  const handleToggleActive = useCallback(
    (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
      dispatch(setTemplateField('isActive', !!checked));
    },
    [dispatch]
  );

  const handleVariableInsert = useCallback(
    (variableText: string) => {
      const editor = roosterEditorRef.current;
      if (!editor) return;
      editor.focus();
      editor.insertContent(variableText);
      dispatch(setTemplateField('emailBody', buildFullDocument(editor.getContent())));
    },
    [dispatch, buildFullDocument]
  );

  const variableMenuProps = React.useMemo(
    () => ({
      items: variables.map((variable) => ({
        key: variable.name,
        text: variable.name,
        title: `${variable.description} — Example: ${variable.example}`,
        onClick: () => handleVariableInsert(`{{${variable.name}}}`),
      })),
    }),
    [variables, handleVariableInsert]
  );

  if (!selectedTemplate) return null;

  return (
    <>
      <div
        className={styles.overlay}
        onClick={handleClose}
        role="presentation"
        aria-hidden="true"
      />
      <div
        className={styles.editPanelContainer}
        role="dialog"
        aria-label={`Edit template: ${selectedTemplate.templateName}`}
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.editPanelHeader}>
          <div>
            <h2 className={styles.editPanelHeaderTitle}>{selectedTemplate.templateName}</h2>
            <div className={styles.editPanelHeaderCode}>{selectedTemplate.templateCode}</div>
          </div>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={handleClose}
            ariaLabel="Close edit panel"
          />
        </div>

        {/* Body */}
        <div className={styles.editPanelBody}>
          {saveSuccess && (
            <MessageBar
              messageBarType={MessageBarType.success}
              onDismiss={() => dispatch(clearSaveStatus())}
              dismissButtonAriaLabel="Dismiss success message"
              styles={{ root: { marginBottom: '12px' } }}
            >
              Template saved successfully.
            </MessageBar>
          )}
          {error && (
            <MessageBar
              messageBarType={MessageBarType.error}
              onDismiss={() => dispatch(clearSaveStatus())}
              dismissButtonAriaLabel="Dismiss error message"
              styles={{ root: { marginBottom: '12px' } }}
            >
              {error}
              {correlationId && (
                <span style={{ marginLeft: '8px', fontSize: '11px', color: '#a19f9d' }}>
                  Correlation ID: {correlationId}
                </span>
              )}
            </MessageBar>
          )}

          {/* Read-only info */}
          <div className={styles.readOnlyGrid}>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Template Code</span>
              <span className={styles.readOnlyValue}>{selectedTemplate.templateCode}</span>
            </div>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Recipient Type</span>
              <span className={getBadgeClass(selectedTemplate.recipientType)}>
                {selectedTemplate.recipientType}
              </span>
            </div>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Trigger Event</span>
              <span className={styles.readOnlyValue}>{selectedTemplate.triggerEvent}</span>
            </div>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Suppression Rule</span>
              <span className={styles.readOnlyValue}>{selectedTemplate.suppressionRule}</span>
            </div>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Attachments</span>
              <span className={styles.readOnlyValue}>
                {selectedTemplate.hasAttachments
                  ? selectedTemplate.attachmentDescription || 'Yes'
                  : 'None'}
              </span>
            </div>
            <div className={styles.readOnlyField}>
              <span className={styles.readOnlyLabel}>Geographic Scope</span>
              <span className={styles.readOnlyValue}>
                {selectedTemplate.geographicScope || 'Global'}
              </span>
            </div>
          </div>

          {/* Editable fields */}
          <h3 className={styles.sectionTitle}>Editable Fields</h3>

          <div className={styles.fieldGroup}>
            <TextField
              label="Email Subject"
              value={selectedTemplate.emailSubject}
              onChange={handleSubjectChange}
              ariaLabel="Email subject line"
              description="Use {{variable}} syntax for dynamic content"
            />
          </div>

          <VariableInsertPanel onInsert={handleVariableInsert} />

          <div className={styles.fieldGroup}>
            <RichTextEditor
              label="Email Body"
              ariaLabel="Email body editor"
              value={editorBody}
              onChange={handleEditorChange}
              theme={CoherenceV9LightTheme}
              commandBarButtons={[
                <BoldButton key="bold" theme={CoherenceV9LightTheme} />,
                <ItalicButton key="italic" theme={CoherenceV9LightTheme} />,
                <UnderlineButton key="underline" theme={CoherenceV9LightTheme} />,
                <BulletListButton key="bulletList" theme={CoherenceV9LightTheme} />,
                <NumberedListButton key="numberedList" theme={CoherenceV9LightTheme} />,
                <CreateLinkButton key="createLink" theme={CoherenceV9LightTheme} />,
                <DefaultButton
                  key="insertVariable"
                  text="Insert Variable"
                  iconProps={{ iconName: 'Add' }}
                  menuProps={variableMenuProps}
                  disabled={variables.length === 0}
                  aria-label="Insert variable"
                  title="Insert a template variable at the cursor"
                  styles={{ root: { height: '32px', minWidth: 'auto', padding: '0 8px', border: 'none', backgroundColor: 'transparent' } }}
                />,
              ]}
              editorRef={(editor) => { roosterEditorRef.current = editor; }}
              resizable
              characterMax={100000}
            />
          </div>

          <div className={styles.fieldGroup}>
            <Dropdown
              label="CC Recipients"
              multiSelect
              selectedKeys={selectedTemplate.ccRecipients}
              options={ccBccOptions}
              onChange={handleCcChange}
              ariaLabel="CC recipients"
              placeholder="Select CC recipients"
            />
          </div>

          <div className={styles.fieldGroup}>
            <Dropdown
              label="BCC Recipients"
              multiSelect
              selectedKeys={selectedTemplate.bccRecipients}
              options={ccBccOptions}
              onChange={handleBccChange}
              ariaLabel="BCC recipients"
              placeholder="Select BCC recipients"
            />
          </div>

          <div className={styles.fieldGroup}>
            <Toggle
              label="Active"
              checked={selectedTemplate.isActive}
              onChange={handleToggleActive}
              onText="Active"
              offText="Inactive"
              ariaLabel="Toggle template active state"
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.editPanelFooter}>
          <div className={styles.footerButtons}>
            <DefaultButton
              text="Discard"
              onClick={() => {
                if (hasUnsavedChanges()) {
                  setShowDiscardDialog(true);
                } else {
                  dispatch(selectTemplate(null));
                  setOriginalTemplate(null);
                }
              }}
              ariaLabel="Discard changes"
            />
            <DefaultButton
              text="Preview"
              onClick={handlePreview}
              ariaLabel="Preview email"
              iconProps={{ iconName: 'View' }}
            />
            {canManageTemplates && (
              <PrimaryButton
                text={saving ? 'Saving...' : 'Save Changes'}
                onClick={handleSave}
                disabled={saving}
                ariaLabel="Save template changes"
                iconProps={{ iconName: 'Save' }}
              />
            )}
            {saving && <Spinner size={SpinnerSize.small} ariaLabel="Saving template" />}
          </div>
          {selectedTemplate.lastModifiedDate && (
            <span className={styles.lastSaved}>
              Last saved: {new Date(selectedTemplate.lastModifiedDate).toLocaleString()}
              {selectedTemplate.lastModifiedBy ? ` by ${selectedTemplate.lastModifiedBy}` : ''}
            </span>
          )}
        </div>
      </div>

      {/* Discard confirmation dialog */}
      <Dialog
        hidden={!showDiscardDialog}
        onDismiss={() => setShowDiscardDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Discard Changes?',
          subText: 'You have unsaved changes. Are you sure you want to discard them?',
        }}
        modalProps={{ isBlocking: true }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDiscard} text="Discard" ariaLabel="Confirm discard changes" />
          <DefaultButton onClick={() => setShowDiscardDialog(false)} text="Cancel" ariaLabel="Cancel discard" />
        </DialogFooter>
      </Dialog>

      {/* Preview Modal */}
      {showPreview && (
        <EmailTemplatePreview
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          previewResult={previewResult}
          loading={previewing}
          error={error}
        />
      )}
    </>
  );
};
